import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ApiService } from '../../services/api.service';
import { ApiResponse } from '../../shared/types';
import {
  readResponsePagination,
  resolveListPagination,
} from '../../shared/utils/list-pagination.util';

export type AuditTargetType = 'admin' | 'attendant' | 'staff';
export type AuditAction = 'create' | 'update' | 'delete' | 'deactivate' | 'role_change';

export interface AuditLogEntry {
  id: string;
  company_id: string;
  company_name: string;
  action: AuditAction;
  target_type: AuditTargetType;
  target_user_id: string | null;
  target_employee_id: string | null;
  target_name: string | null;
  target_email: string | null;
  performed_by: string;
  performed_by_name: string;
  performed_by_email: string;
  payload: { before?: Record<string, any>; after?: Record<string, any> };
  created_at: string;
}

export interface AuditLogListParams {
  page?: number;
  limit?: number;
  company_id?: string;
  target_type?: AuditTargetType;
  action?: AuditAction;
  date_from?: string;
  date_to?: string;
  search?: string;
}

export interface AuditLogListResult {
  entries: AuditLogEntry[];
  totalItems: number;
  currentPage: number;
  totalPages: number;
  itemsPerPage: number;
}

@Injectable({ providedIn: 'root' })
export class AuditLogService {
  constructor(private apiService: ApiService) {}

  listAuditLog(params: AuditLogListParams = {}): Observable<ApiResponse<AuditLogListResult>> {
    const page = params.page ?? 1;
    const limit = params.limit ?? 25;

    const queryParams: Record<string, string> = {};
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        queryParams[key] = String(value);
      }
    });

    return this.apiService
      .getWithSuperAdminToken<AuditLogEntry[]>('admin/super-admin/audit-log', queryParams)
      .pipe(
        map(res => {
          const entries = Array.isArray(res.data) ? res.data : [];
          const pagination = resolveListPagination(
            readResponsePagination(res),
            { page, limit },
            entries.length
          );
          return {
            ...res,
            data: { entries, ...pagination },
          };
        }),
        catchError(error => {
          console.error('Error fetching audit log:', error);
          return throwError(() => error);
        })
      );
  }
}
